import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, RSD, imgURL } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useOrderSocket } from "../../hooks/useOrderSocket";
import { BagLogo, BagLogoTile } from "../../components/Logo";
import ProductDrawer from "./ProductDrawer";
import CartSheet from "./CartSheet";
import CheckoutSheet from "./CheckoutSheet";
import { ShoppingBag, ArrowLeft, Ban } from "lucide-react";
import { useI18n } from "../../context/I18nContext";
import { L } from "../../lib/localize";
import { track, impressionObserver } from "../../lib/analytics";
import { RestaurantOperationalInfo } from "../../components/RestaurantOperationalInfo";
import RestaurantInfoSheet from "../../components/RestaurantInfoSheet";

function Placeholder({ name }) {
  return <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-yellow to-brand-orange"><span className="font-display font-black text-white/90 text-lg px-2 text-center leading-tight">{name}</span></div>;
}

export default function RestaurantPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { locale, t, localePath, switchLocale } = useI18n();
  const { items, count, total, addItem, forceReplace, restaurant: cartRestaurant } = useCart();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/restaurants/${slug}/menu`);
      setRestaurant(data.restaurant); setCategories(data.categories); setProducts(data.products);
      if (data.categories.length) setActiveCat(data.categories[0].id);
      track("restaurant_view", { restaurant_id: data.restaurant?.id, city_id: data.restaurant?.city_id,
                                 dedupe_key: data.restaurant?.id });
    } catch { setNotFound(true); }
  };
  useEffect(() => { load(); }, [slug]);

  // Quick reorder deep-link: open the rebuilt cart for review straight away.
  useEffect(() => {
    if (restaurant && new URLSearchParams(window.location.search).get("cart") === "1") {
      setCartOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [restaurant]);
  // Menu category views (from the scroll-spy / tab selection) — once per category per session.
  useEffect(() => {
    if (activeCat && restaurant) {
      track("menu_category_view", { restaurant_id: restaurant.id, category_id: activeCat,
                                    dedupe_key: `${restaurant.id}:${activeCat}` });
    }
  }, [activeCat, restaurant]);

  // Product impressions: only products actually seen on screen, once each per session.
  const prodIoRef = useRef(null);
  const prodMetaRef = useRef(new Map());
  useEffect(() => {
    prodIoRef.current = impressionObserver("product_impression", (el) => {
      const p = prodMetaRef.current.get(el);
      return p ? { restaurant_id: restaurant?.id, product_id: p.id, category_id: p.category_id,
                   dedupe_key: p.id } : {};
    });
    return () => prodIoRef.current?.disconnect();
  }, [restaurant]);
  const registerProduct = (el, p) => {
    if (!el || !prodIoRef.current) return;
    prodMetaRef.current.set(el, p);
    prodIoRef.current.observe(el);
  };

  const clickLockRef = useRef(0);
  // Scroll-spy: highlight the category whose section is currently under the sticky header.
  useEffect(() => {
    if (!categories.length) return;
    const obs = new IntersectionObserver((entries) => {
      if (Date.now() < clickLockRef.current) return;
      const visible = entries.filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveCat(visible[0].target.id.replace("cat-sec-", ""));
    }, { rootMargin: "-150px 0px -60% 0px", threshold: 0 });
    categories.forEach((c) => { const el = document.getElementById(`cat-sec-${c.id}`); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [categories, products]);

  // Keep the active pill visible in the horizontal bar (no vertical page movement).
  useEffect(() => {
    if (!activeCat) return;
    document.querySelector(`[data-testid="cat-${activeCat}"]`)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeCat]);

  useOrderSocket((msg) => {
    if (msg.type === "product_update" && msg.product?.restaurant_id === restaurant?.id) setProducts((prev) => prev.map((p) => (p.id === msg.product.id ? msg.product : p)));
    if (msg.type === "menu_update" && msg.restaurant_id === restaurant?.id) load();
    if (msg.type === "settings_update" && msg.restaurant_id === restaurant?.id) api.get(`/restaurants/${slug}`).then(({ data }) => setRestaurant(data)).catch(() => {});
  });

  const grouped = useMemo(() => {
    const by = {};
    for (const c of categories) by[c.id] = products.filter((p) => p.category_id === c.id);
    return by;
  }, [categories, products]);

  const restMeta = restaurant ? { slug: restaurant.slug, name: restaurant.name, id: restaurant.id, payment_method: restaurant.payment_method } : null;

  const handleAdd = (product, ids, qty, note, person) => {
    const res = addItem(product, ids, qty, note, restMeta, person);
    if (res === "conflict") { setConflict({ product, ids, qty, note, person }); return false; }
    track("add_to_cart", { restaurant_id: restaurant?.id, product_id: product.id,
                           category_id: product.category_id, quantity: qty,
                           discounted: !!product.happy_hour });
    return true;
  };

  const openProduct = (p) => {
    setSelected(p);
    track("product_view", { restaurant_id: restaurant?.id, product_id: p.id,
                            category_id: p.category_id, dedupe_key: p.id });
  };

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3"><BagLogo size={56} /><p className="font-display font-bold">{t("rp.not_found", "Restoran nije pronađen")}</p><a href={locale === "en" ? "/en" : "/"} className="text-brand-red font-semibold underline">{t("rp.back_ponesi", "Nazad na Ponesi")}</a></div>
  );
  if (!restaurant) return <div className="min-h-screen flex items-center justify-center bg-brand-red"><div className="animate-pop"><BagLogoTile size={88} /></div></div>;

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="max-w-md mx-auto bg-[#F9F9F7] min-h-screen shadow-2xl relative pb-28">
        <div className="relative h-48">
          {restaurant.cover_image ? <img src={imgURL(restaurant.cover_image)} alt={restaurant.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-brand-orange to-brand-red" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button data-testid="back-home" onClick={() => navigate("/")} className="absolute top-4 left-4 bg-white/90 rounded-full p-2.5 shadow"><ArrowLeft size={18} /></button>
          <div data-testid="rp-lang-switch" className="absolute top-4 right-4 flex bg-white/90 rounded-full p-0.5 shadow text-xs font-bold">
            <button data-testid="rp-lang-sr" onClick={() => switchLocale("sr")} className={`px-2.5 py-1 rounded-full transition-colors ${locale === "sr" ? "bg-brand-red text-white" : "text-slate-600"}`}>SR</button>
            <button data-testid="rp-lang-en" onClick={() => switchLocale("en")} className={`px-2.5 py-1 rounded-full transition-colors ${locale === "en" ? "bg-brand-red text-white" : "text-slate-600"}`}>EN</button>
          </div>
          <div className="absolute bottom-3 left-5 right-5 text-white">
            <div className="flex items-center gap-2">
              <h1 data-testid="restaurant-name" className="font-display font-black text-2xl">{restaurant.name}</h1>
              <span data-testid="restaurant-status" className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${restaurant.open_now ? (restaurant.accepting_orders ? "bg-brand-green" : "bg-brand-orange") : "bg-slate-600"}`}>
                {!restaurant.open_now ? t("badge.closed", "Zatvoreno") : restaurant.accepting_orders ? t("badge.open", "Otvoreno") : t("badge.paused", "Pauza")}
              </span>
            </div>
            <p className="text-white/85 text-sm">{restaurant.category}</p>
          </div>
        </div>

        <div className="px-5 py-4 bg-white border-b flex items-start gap-2">
          <RestaurantOperationalInfo restaurant={restaurant} locale={locale} t={t} className="flex-1 min-w-0" />
          <button data-testid="restaurant-info-btn" onClick={() => setInfoOpen(true)}
            aria-label={t("info.title", "Informacije o restoranu")} title={t("info.title", "Informacije o restoranu")}
            className="-mt-1 -mr-1 w-11 h-11 shrink-0 flex items-center justify-center rounded-full hover:bg-slate-100 active:scale-95 transition-colors">
            <img src="/info-icon-72.png" srcSet="/info-icon-72.png 1x, /info-icon-144.png 2x" alt="" aria-hidden="true"
              className="w-6 h-6 object-contain select-none pointer-events-none" />
          </button>
        </div>

        {!restaurant.can_order && (
          <div data-testid="closed-banner" className="mx-5 mt-4 bg-slate-900 text-white rounded-2xl px-4 py-3 text-center">
            <p className="font-display font-black text-sm">{!restaurant.open_now ? t("rp.closed_now", "TRENUTNO SMO ZATVORENI") : t("rp.not_accepting", "TRENUTNO NE PRIMAMO PORUDŽBINE")}</p>
          </div>
        )}

        <div className="sticky top-0 z-20 bg-[#F9F9F7]/95 backdrop-blur pt-4 pb-2">
          <h2 className="px-5 font-display font-black text-lg mb-2">{t("rp.menu", "Meni")}</h2>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide px-5">
            {categories.filter((c) => (grouped[c.id] || []).length).map((c) => (
              <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => { clickLockRef.current = Date.now() + 800; setActiveCat(c.id); document.getElementById(`cat-sec-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-colors ${activeCat === c.id ? "bg-brand-red text-white" : "bg-white text-slate-600"}`}>{L(c, locale)}</button>
            ))}
          </div>
        </div>

        <main className="px-5 mt-2 space-y-8">
          {categories.filter((c) => (grouped[c.id] || []).length).map((c) => (
            <section key={c.id} id={`cat-sec-${c.id}`} style={{ scrollMarginTop: "140px" }}>
              <h3 className="font-display font-black text-xl text-slate-900 mb-3">{L(c, locale)}</h3>
              <div className="space-y-3">
                {(grouped[c.id] || []).map((p) => (
                  <button key={p.id} data-testid={`product-${p.id}`} ref={(el) => registerProduct(el, p)} disabled={!p.available} onClick={() => openProduct(p)}
                    className={`w-full text-left bg-white rounded-3xl p-3 flex gap-3 items-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-transform active:scale-[0.98] ${!p.available ? "opacity-60" : ""}`}>
                    <div className="w-32 aspect-[4/3] rounded-2xl overflow-hidden flex-shrink-0 relative">
                      {p.image ? <img src={imgURL(p.image)} alt={L(p, locale)} className="w-full h-full object-cover" /> : <Placeholder name={L(p, locale)} />}
                      {!p.available && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><span className="text-white text-[10px] font-bold uppercase flex items-center gap-1"><Ban size={12} /> {t("rp.unavailable", "Nije dostupno")}</span></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display font-bold text-base text-slate-900 leading-tight">{L(p, locale)}</h4>
                      <p className="text-slate-500 text-xs mt-1 line-clamp-2">{L(p, locale, "description")}</p>
                      <div className="mt-2 font-display font-black text-brand-red flex flex-wrap items-center gap-2">
                        {p.happy_hour ? (
                          <>
                            <span data-testid={`price-base-${p.id}`} className="line-through text-red-500 text-xs font-bold">{RSD(p.happy_hour.base_price)}</span>
                            <span data-testid={`price-hh-${p.id}`} className="text-brand-green text-xl">{RSD(p.happy_hour.price)}</span>
                            <span data-testid={`promo-tag-${p.id}`} className={`text-[10px] font-black uppercase rounded-full px-2 py-0.5 ${p.happy_hour.product_discount ? "bg-brand-green text-white" : p.happy_hour.store_wide ? "bg-brand-red text-white" : "bg-amber-400 text-slate-900"}`}>
                              {p.happy_hour.product_discount
                                ? (p.happy_hour.discount_type === "PERCENTAGE" ? `-${p.happy_hour.discount_value}%` : `-${RSD(p.happy_hour.discount_value)}`)
                                : p.happy_hour.store_wide ? `-${p.happy_hour.discount_percent}%` : "Happy Hour"}
                            </span>
                          </>
                        ) : RSD(p.price)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
          <div className="h-4" />
        </main>

        {count > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-40 max-w-md mx-auto p-4">
            <button data-testid="open-cart-btn" onClick={() => setCartOpen(true)} className="w-full bg-brand-red text-white rounded-full px-5 py-4 flex items-center justify-between shadow-xl active:scale-[0.98] transition-transform">
              <span className="flex items-center gap-2 font-bold"><span className="bg-white text-brand-red rounded-full w-6 h-6 flex items-center justify-center text-sm">{count}</span><ShoppingBag size={18} /> {t("rp.cart", "Korpa")}</span>
              <span className="font-display font-black">{RSD(total)}</span>
            </button>
          </div>
        )}

        <ProductDrawer product={selected} restaurant={restMeta} onAdd={handleAdd} onClose={() => setSelected(null)} />
        <RestaurantInfoSheet open={infoOpen} onClose={() => setInfoOpen(false)} restaurant={restaurant} locale={locale} t={t} />
        <CartSheet open={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); track("checkout_started", { restaurant_id: restaurant?.id, items: items.length, total }); }} />
        <CheckoutSheet open={checkoutOpen} onClose={() => setCheckoutOpen(false)} onEdit={() => { setCheckoutOpen(false); setCartOpen(true); }} restaurant={restaurant}
          canOrder={restaurant.can_order} blockMsg={!restaurant.open_now ? t("rp.closed_block", "Restoran je zatvoren") : t("rp.not_accepting_block", "Ne primamo porudžbine")}
          onPlaced={(order) => {
            const qrRid = (() => { try { return sessionStorage.getItem("ponesi_quick_reorder_rid"); } catch { return null; } })();
            if (qrRid && qrRid === restaurant?.id) {
              track("quick_reorder_completed", { restaurant_id: restaurant?.id, dedupe_key: order.id });
              try { sessionStorage.removeItem("ponesi_quick_reorder_rid"); } catch {}
            }
            setCheckoutOpen(false); navigate(localePath(`/order/${order.id}`));
          }} />

        {conflict && (
          <div className="fixed inset-0 z-50 max-w-md mx-auto flex items-end" data-testid="cart-conflict">
            <div className="absolute inset-0 bg-black/50" onClick={() => setConflict(null)} />
            <div className="relative w-full bg-white rounded-t-3xl p-6 animate-slide-up">
              <p className="text-slate-700">{t("rp.conflict_body_1", "Vaša korpa trenutno sadrži proizvode iz restorana")} <b>{cartRestaurant?.name}</b>. {t("rp.conflict_body_2", "Želite li da ispraznite korpu i nastavite sa drugim restoranom?")}</p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button data-testid="conflict-cancel" onClick={() => setConflict(null)} className="rounded-full py-3 bg-slate-100 font-bold">{t("rp.conflict_cancel", "Otkaži")}</button>
                <button data-testid="conflict-clear" onClick={() => { forceReplace(conflict.product, conflict.ids, conflict.qty, conflict.note, restMeta, conflict.person); setConflict(null); setSelected(null); }} className="rounded-full py-3 bg-brand-red text-white font-bold">{t("rp.conflict_clear", "Isprazni korpu")}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
